import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { logToSentry } from '~/helpers/loggers.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useSetPasscodeMutation } from '~/store/apis/endpoints/onboardingApi/passcode/passcode.api';
import {
  PasscodeView,
  selectPasscodeView,
  setCreateValue,
  setSuccess,
  setVerifyValue,
  showMismatch,
  showSubmitError,
  startCreate,
  startVerify,
} from '~/store/slices/passcode.slice';

// Shared across every component that submits or observes the submission (the offer
// step's footer triggers it, its content shows the resulting error), so all of them
// see the same isLoading/isError regardless of which one made the call.
const SUBMIT_CACHE_KEY = 'passcode-submit';

/**
 * The create-passcode view and its transitions. The view is derived at render
 * time: the slice only ever holds a local override once the user has acted,
 * defaulting to `hasExistingPasscode` otherwise. This means the offer step's
 * content and footer — rendered as independent siblings by `Stepper` — always
 * agree in the very first render the GET status is available in. No effect is
 * needed to seed the slice from server data.
 */
export const usePasscodeFlow = (hasExistingPasscode: boolean) => {
  const dispatch = useAppDispatch();
  const localView = useSelector(selectPasscodeView);
  const view: PasscodeView =
    localView ??
    (hasExistingPasscode
      ? { kind: 'alreadySet' }
      : { kind: 'create', passcode: '' });

  const [
    submitPasscode,
    { isLoading: isSubmitting, reset: resetSubmit },
  ] = useSetPasscodeMutation({ fixedCacheKey: SUBMIT_CACHE_KEY });

  // The "back" and "create new" buttons share this: both return to an empty
  // create view and discard any error from a previous attempt.
  const goToCreate = useCallback(() => {
    resetSubmit();
    dispatch(startCreate());
  }, [dispatch, resetSubmit]);

  const setCreatePasscode = useCallback(
    (passcode: string) => dispatch(setCreateValue(passcode)),
    [dispatch]
  );

  const proceedToVerify = useCallback(() => {
    if (view.kind !== 'create') return;
    dispatch(startVerify({ passcode: view.passcode }));
  }, [view, dispatch]);

  const setVerifyConfirmation = useCallback(
    (confirmation: string) => {
      if (view.kind !== 'verify') return;
      dispatch(setVerifyValue({ passcode: view.passcode, confirmation }));
    },
    [view, dispatch]
  );

  const submitVerification = useCallback(async () => {
    if (view.kind !== 'verify') return;
    if (view.confirmation !== view.passcode) {
      dispatch(showMismatch({ passcode: view.passcode }));
      return;
    }
    try {
      await submitPasscode({ passcode: view.passcode }).unwrap();
      dispatch(setSuccess());
    } catch (error) {
      // The view drives the message shown; the passcode itself is never logged.
      logToSentry(error, 'setPasscode');
      dispatch(showSubmitError());
    }
  }, [view, dispatch, submitPasscode]);

  return {
    view,
    isSubmitting,
    goToCreate,
    setCreatePasscode,
    proceedToVerify,
    setVerifyConfirmation,
    submitVerification,
  };
};
